import { useId } from 'react';
import { OUTCOME_TONES } from '../../lib/story/types';
import type { ToneMix } from '../../lib/story/chainTree';
import { parseTemplate, roleKnown } from '../../lib/story/template';
import { inputCls } from '../forms/fields';
import { TONE_COLORS } from '../../lib/story/ui';

/** Template text with tokens highlighted: known roles cyan, unknown roles and bad tokens red. */
export function TemplateText({ text, roles }: { text: string; roles?: Set<string> }) {
  const segs = parseTemplate(text);
  return (
    <span className="whitespace-pre-wrap" data-testid="template-text">
      {segs.map((s, i) =>
        s.kind === 'text' ? (
          <span key={i}>{s.text}</span>
        ) : s.kind === 'token' && (!roles || roleKnown(s.role, roles)) ? (
          <mark
            key={i}
            className="rounded bg-nonphoto/60 px-0.5 font-mono text-ink"
            title={`${s.role} · ${s.field}`}
          >
            {s.text}
          </mark>
        ) : (
          <mark
            key={i}
            className="rounded bg-blam/20 px-0.5 font-mono text-blam"
            title={s.kind === 'bad' ? s.error : `'${s.kind === 'token' ? s.role : ''}' isn't cast`}
          >
            {s.text}
          </mark>
        ),
      )}
    </span>
  );
}

/** A text input (or textarea) for template text with the highlighted reading underneath. */
export function TemplateInput({
  label,
  value,
  onChange,
  roles,
  multiline,
  hint,
  max,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  roles?: Set<string>;
  multiline?: boolean;
  hint?: string;
  max?: number;
}) {
  const id = useId();
  const over = max !== undefined && value.length > max;
  return (
    <div className="sm:col-span-2">
      <label htmlFor={id} className="block text-xs font-semibold uppercase tracking-wide">
        {label}
      </label>
      {multiline ? (
        <textarea
          id={id}
          aria-invalid={over}
          className={`${inputCls} h-14`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <input
          id={id}
          aria-invalid={over}
          className={inputCls}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
      {value && (
        <p className="mt-0.5 text-xs">
          <TemplateText text={value} roles={roles} />
          {max !== undefined && (
            <span className={`ml-2 ${over ? 'font-semibold text-blam' : 'text-bruise'}`}>
              {value.length}/{max}
            </span>
          )}
        </p>
      )}
      {hint && <p className="mt-0.5 text-xs text-bruise">{hint}</p>}
    </div>
  );
}

const pctText = (v: number) => `${Math.round(v * 100)}`;

/**
 * A five-part tone bar (disaster → windfall), reach-weighted shares 0..1, with the target mix (percent) drawn as tick marks above it so
 * the gap reads at a glance.
 */
export function ToneMixBar({
  mix,
  target,
  label,
  height = 12,
}: {
  mix: ToneMix;
  target?: ToneMix;
  label?: string;
  height?: number;
}) {
  const total = OUTCOME_TONES.reduce((s, t) => s + mix[t], 0);
  const tTotal = target ? OUTCOME_TONES.reduce((s, t) => s + target[t], 0) : 0;
  let acc = 0;
  let tAcc = 0;
  return (
    <div className="text-xs" data-testid="tone-mix">
      {label && <div className="font-semibold">{label}</div>}
      <svg
        width="100%"
        viewBox={`0 0 100 ${height + 4}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={`${label ?? 'Tone mix'}: ${OUTCOME_TONES.map((t) => `${t} ${pctText(total ? mix[t] / total : 0)}%`).join(', ')}`}
      >
        <rect
          x={0}
          y={4}
          width={100}
          height={height}
          fill="#ffffff"
          stroke="#15121a"
          strokeWidth={0.3}
        />
        {total > 0 &&
          OUTCOME_TONES.map((t) => {
            const w = (mix[t] / total) * 100;
            const x = acc;
            acc += w;
            return w > 0 ? (
              <rect key={t} x={x} y={4} width={w} height={height} fill={TONE_COLORS[t]} />
            ) : null;
          })}
        {target &&
          tTotal > 0 &&
          OUTCOME_TONES.slice(0, -1).map((t) => {
            tAcc += (target[t] / tTotal) * 100;
            return (
              <path
                key={t}
                d={`M${tAcc} 0 V${height + 4}`}
                stroke="#15121a"
                strokeWidth={0.6}
                strokeDasharray="1 0.6"
              />
            );
          })}
      </svg>
      <div className="flex flex-wrap gap-x-2 font-mono">
        {OUTCOME_TONES.map((t) => (
          <span key={t}>
            <span className="mr-0.5 inline-block h-2 w-2" style={{ background: TONE_COLORS[t] }} />
            {t} {pctText(total ? mix[t] / total : 0)}
            {target && tTotal > 0 && (
              <span className="text-bruise">/{pctText(target[t] / tTotal)}</span>
            )}
          </span>
        ))}
      </div>
    </div>
  );
}
